import {ErrorI18n} from "../i18n";

export class FailedValidation {
    field: string;
    message: string | null;
    messageArgs: Record<string, unknown> | null;

    /**
     * Create container for failed validation
     * @param field field identifier
     * @param message optional message key for customized error message
     * @param messageArgs optional arguments for message key
     */
    constructor(field: string, message?: string | null, messageArgs?: Record<string, unknown> | null) {
        this.field = field;
        this.message = message || null;
        this.messageArgs = typeof messageArgs === "object" ? {...messageArgs} : null;
    }
}

/** Accepted shapes for failed validations passed to {@link ValidationErrorI18n}. */
type FailedValidationsInput = FailedValidation | string | (FailedValidation | string)[];

function _mapFailedValidations(failedValidations: FailedValidationsInput): FailedValidation[] {
    if (Array.isArray(failedValidations)) {
        return failedValidations
            .map((failedValidation) => {
                if (failedValidation instanceof FailedValidation) {
                    return failedValidation;
                }
                if (typeof failedValidation === "string") {
                    return new FailedValidation(failedValidation);
                }
                return null;
            })
            .filter((fv): fv is FailedValidation => !!fv);
    }
    if (failedValidations instanceof FailedValidation) {
        return [failedValidations];
    }
    if (typeof failedValidations === "string") {
        return [new FailedValidation(failedValidations)];
    }
    return [];
}

export class ValidationErrorI18n extends ErrorI18n {
    failedValidations: FailedValidation[];

    /**
     * Create Validation error with message.
     * @param failedValidations failed validations
     * @param errorKey error key (i18n)
     * @param errorArgs optional error args (i18n)
     */
    constructor(
        failedValidations: FailedValidationsInput,
        errorKey: string,
        errorArgs?: Record<string, unknown> | null,
    ) {
        super(errorKey, errorArgs);
        this.failedValidations = _mapFailedValidations(failedValidations);
    }

    /**
     * Return all fields with validation errors.
     * @return list of fields with validation errors
     */
    failedFields(): string[] {
        return this.failedValidations.map((f) => f.field);
    }
}

/**
 * Callback that receives a validation error and dispatches it (e.g. into a Redux error reducer).
 * @param error error i18n key — never null; unspecific errors use "general.error.unspecific"
 * @param errorArgs error args (i18n)
 * @param failedFields array of invalid fields found during validation
 */
export type HandleValidationError = (
    error: string,
    errorArgs: Record<string, unknown> | null,
    failedFields: string[],
) => void;
